import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Avatar,
  Box,
} from '@mui/material';
import logo from '../../logo2.png';
import {
  Chat as ChatIcon,
  Search as SearchIcon,
  CloudUpload as UploadIcon,
  Logout as LogoutIcon,
  RateReview as ReviewIcon,
  StickyNote2 as NoteIcon,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';

interface NavbarProps {
  currentView: string;
  onViewChange: (view: string) => void;
  onMyNotesClick?: () => void;
}

const Navbar: React.FC<NavbarProps> = ({ currentView, onViewChange, onMyNotesClick }) => {
  const { user, logout, isAdmin } = useAuth();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    handleMenuClose();
  };

  // Filter navigation items based on user role
  const allNavItems = [
    { id: 'chat', label: 'Chat', icon: <ChatIcon /> },
    { id: 'search', label: 'Search', icon: <SearchIcon />, adminOnly: true },
    { id: 'upload', label: 'Upload', icon: <UploadIcon />, adminOnly: true },
    { id: 'mynotes', label: 'My Notes', icon: <NoteIcon />, special: true }, // Special handling
    { id: 'review', label: 'Review', icon: <ReviewIcon />, adminOnly: true },
  ];

  const navItems = allNavItems.filter(item => {
    if (item.adminOnly) return isAdmin;
    return true;
  });

  return (
    <AppBar position="static" elevation={0}>
      <Toolbar sx={{ minHeight: 72, px: 3 }}>
        {/* Logo */}
                  <Box sx={{ display: 'flex', alignItems: 'center', mr: 4 }}>
            <Box 
              component="img"
              src={logo}
              alt="YVB Logo"
              sx={{ 
                height: '32px',
                transition: 'transform 0.3s ease',
                '&:hover': {
                  transform: 'scale(1.1)',
                }
              }}
            />

        </Box>

        {/* Navigation Items */}
        <Box sx={{ flexGrow: 1, display: 'flex', gap: 0.5 }}>
          {navItems.map((item) => {
            const isActive = item.special ? currentView === 'mynotes' : currentView === item.id;
            return (
              <Button
                key={item.id}
                startIcon={item.icon}
                onClick={() => item.special ? onMyNotesClick?.() : onViewChange(item.id)}
                sx={{
                  color: item.special 
                    ? isActive ? 'success.dark' : 'success.main'
                    : isActive ? 'primary.main' : 'text.secondary',
                  backgroundColor: isActive
                    ? item.special
                      ? 'rgba(5, 150, 105, 0.12)'
                      : 'rgba(37, 99, 235, 0.08)'
                    : item.special
                      ? 'rgba(5, 150, 105, 0.04)'
                      : 'transparent',
                  border: isActive
                    ? item.special
                      ? '1px solid rgba(5, 150, 105, 0.3)'
                      : '1px solid rgba(37, 99, 235, 0.2)'
                    : item.special
                      ? '1px solid rgba(5, 150, 105, 0.15)'
                      : '1px solid transparent',
                  fontWeight: isActive ? 600 : 500,
                  borderRadius: 2,
                  px: 3,
                  py: 1,
                  minWidth: 'auto',
                  '&:hover': {
                    backgroundColor: item.special
                      ? 'rgba(5, 150, 105, 0.15)'
                      : isActive 
                        ? 'rgba(37, 99, 235, 0.12)' 
                        : 'rgba(100, 116, 139, 0.08)',
                    color: item.special
                      ? 'success.dark'
                      : isActive ? 'primary.main' : 'text.primary',
                    transform: 'translateY(-1px)',
                    boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
                  },
                  transition: 'all 0.2s ease-in-out',
                }}
              >
                {item.label}
              </Button>
            );
          })}
        </Box>

        {/* User Menu */}
        {user && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ textAlign: 'right', display: { xs: 'none', sm: 'block' } }}>
              <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                {user.first_name ? `${user.first_name} ${user.last_name}` : user.username}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                Veterinary Professional
              </Typography>
            </Box>
            <IconButton
              size="large"
              edge="end"
              aria-label="account of current user"
              aria-controls="primary-search-account-menu"
              aria-haspopup="true"
              onClick={handleMenuOpen}
              sx={{
                p: 0.5,
                '&:hover': {
                  backgroundColor: 'rgba(37, 99, 235, 0.08)',
                },
                transition: 'all 0.2s ease-in-out',
              }}
            >
              <Avatar 
                sx={{ 
                  width: 40, 
                  height: 40, 
                  bgcolor: 'linear-gradient(45deg, #2563eb, #059669)',
                  background: 'linear-gradient(45deg, #2563eb, #059669)',
                  fontWeight: 600,
                  fontSize: '1rem',
                  border: '2px solid rgba(37, 99, 235, 0.1)',
                }}
              >
                {(user.first_name?.[0] || user.username[0]).toUpperCase()}
              </Avatar>
            </IconButton>
            
            <Menu
              id="primary-search-account-menu"
              anchorEl={anchorEl}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
              PaperProps={{
                sx: {
                  mt: 1,
                  minWidth: 200,
                  borderRadius: 3,
                  border: '1px solid #e2e8f0',
                  boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)',
                  '& .MuiMenuItem-root': {
                    borderRadius: 1,
                    mx: 1,
                    my: 0.5,
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    '&:hover': {
                      backgroundColor: 'rgba(37, 99, 235, 0.08)',
                    },
                  },
                },
              }}
            >
              <MenuItem 
                onClick={handleLogout}
                sx={{
                  color: 'error.main',
                  '&:hover': {
                    backgroundColor: 'rgba(220, 38, 38, 0.08)',
                  },
                }}
              >
                <LogoutIcon sx={{ mr: 2, fontSize: '1.25rem' }} />
                Logout
              </MenuItem>
            </Menu>
          </Box>
        )}
      </Toolbar>
    </AppBar>
  );
};

export default Navbar; 